/**
 * API Client for GST Autopilot
 * Provides typed API methods for all endpoints
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';



declare global {
    interface Window {
        // Clerk removed
    }
}

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    // 1. Get Token from LocalStorage
    let token = localStorage.getItem('token');

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Required for cross-origin cookie support
    });

    // 2. Interceptor: Handle 401 (Unauthorized)
    if (response.status === 401) {
        try {
            console.log('Session expired, attempting refresh...');
            // Call Refresh Endpoint (HttpOnly Cookie)
            const refreshRes = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });

            if (refreshRes.ok) {
                const data = await refreshRes.json();
                if (data.token) {
                    console.log('Refresh successful');
                    // Store new token
                    localStorage.setItem('token', data.token);
                    token = data.token;

                    // Retry Original Request
                    headers['Authorization'] = `Bearer ${token}`;
                    response = await fetch(url, {
                        ...options,
                        headers,
                        credentials: 'include',
                    });
                }
            } else {
                console.warn('Refresh failed, logging out');
                localStorage.removeItem('token');
                window.location.href = '/sign-in'; // Redirect to login
            }
        } catch (e) {
            console.error('Refresh error', e);
        }
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'API Error' }));
        // Handle standardized error format
        if (error.error && error.error.message) {
            throw new Error(error.error.message);
        }
        if (error.message) {
            throw new Error(error.message);
        }
        throw new Error(error.error?.code || `HTTP ${response.status}`);
    }

    return response.json();
}

// ==========================================
// Transaction Types
// ==========================================

export interface Transaction {
    id: string;
    transactionType: string;
    invoiceNumber: string;
    invoiceDate: string;
    partyGstin?: string;
    partyName?: string;
    hsnCode?: string;
    taxableValue: number;
    cgst: number;
    sgst: number;
    igst: number;
    cess: number;
    totalValue: number;
    placeOfSupply?: string;
    reverseCharge: boolean;
    source: string;
    reconciliationStatus: string;
}

export interface TransactionSummary {
    totalOutward: number;
    totalInward: number;
    netTaxLiability: number;
    itcAvailable: number;
    transactionCount: number;
}

export interface PaginatedResponse<T> {
    transactions: T[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// ==========================================
// Compliance Types
// ==========================================

export interface ComplianceFactor {
    name: string;
    score: number;
    weight: number;
}

export interface ComplianceScore {
    score: number;
    trend: 'up' | 'down' | 'stable';
    change: number;
    factors: ComplianceFactor[];
    lastUpdated: string;
}

export interface AIInsight {
    type: 'warning' | 'tip' | 'success';
    title: string;
    description: string;
    action: string;
}

// ==========================================
// Return Types
// ==========================================

export interface GSTReturn {
    id: string;
    returnType: string;
    returnPeriod: string;
    status: string;
    filedArn?: string;
    filedAt?: string;
}

// ==========================================
// API Methods
// ==========================================

export const api = {
    // Auth
    auth: {
        login: (data: any) =>
            fetchAPI<{ success: boolean; token: string; user: any }>('/auth/login', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        register: (data: any) =>
            fetchAPI<{ success: boolean; token: string; user: any }>('/auth/register', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        googleLogin: (token: string) =>
            fetchAPI<{ success: boolean; token: string; user: any }>('/auth/google', {
                method: 'POST',
                body: JSON.stringify({ token }),
            }),
        forgotPassword: (email: string) =>
            fetchAPI<{ success: boolean; message: string }>('/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email }),
            }),
        resetPassword: (data: any) =>
            fetchAPI<{ success: boolean; message: string }>('/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        verifyEmail: (token: string) =>
            fetchAPI<{ success: boolean; message: string }>('/auth/verify-email', {
                method: 'POST',
                body: JSON.stringify({ token }),
            }),
        sendVerification: () =>
            fetchAPI<{ success: boolean; message: string }>('/auth/send-verification', {
                method: 'POST',
            }),
        refresh: () => fetchAPI<{ success: boolean; token: string }>('/auth/refresh', { method: 'POST' }),
        logout: () => fetchAPI('/auth/logout', { method: 'POST' }),
        me: () => fetchAPI<{ data: any }>('/auth/me'),
    },

    // Transactions
    transactions: {
        list: (params?: { page?: number; limit?: number; type?: string }) =>
            fetchAPI<{ success: boolean; data: PaginatedResponse<Transaction> }>(
                `/transactions?${new URLSearchParams(params as Record<string, string>)}`
            ),

        getById: (id: string) =>
            fetchAPI<{ success: boolean; data: Transaction }>(`/transactions/${id}`),

        create: (data: Partial<Transaction>) =>
            fetchAPI<{ success: boolean; data: Transaction }>('/transactions', {
                method: 'POST',
                body: JSON.stringify(data),
            }),

        getSummary: () =>
            fetchAPI<{ success: boolean; data: TransactionSummary }>('/transactions/summary'),
    },

    // AI & Compliance
    ai: {
        getComplianceScore: () =>
            fetchAPI<{ success: boolean; data: ComplianceScore }>('/ai/compliance-score'),

        getInsights: () =>
            fetchAPI<{ success: boolean; data: AIInsight[] }>('/ai/insights'),

        getRiskAssessment: () =>
            fetchAPI<{ success: boolean; data: any }>('/ai/risk-assessment'),

        getRecommendations: () =>
            fetchAPI<{ success: boolean; data: any[] }>('/ai/recommendations'),
    },

    // Returns
    returns: {
        list: () =>
            fetchAPI<{ success: boolean; data: GSTReturn[] }>('/returns'),

        generate: (returnType: string, period: string) =>
            fetchAPI<{ success: boolean; data: any }>('/returns/generate', {
                method: 'POST',
                body: JSON.stringify({ returnType, period }),
            }),
    },

    // Onboarding
    onboarding: {
        start: (data: any) =>
            fetchAPI<{ success: boolean; data: any }>('/onboarding/start', {
                method: 'POST',
                body: JSON.stringify(data),
            }),

        getProgress: (orgId: string) =>
            fetchAPI<{ success: boolean; data: any }>(`/onboarding/${orgId}/progress`),

        completeStep: (orgId: string, stepName: string, data: any) =>
            fetchAPI<{ success: boolean; data: any }>(`/onboarding/${orgId}/step/${stepName}`, {
                method: 'POST',
                body: JSON.stringify(data),
            }),

        skip: (orgId: string) =>
            fetchAPI<{ success: boolean; message: string }>(`/onboarding/${orgId}/skip`, {
                method: 'POST',
            }),

        resume: (orgId: string) =>
            fetchAPI<{ success: boolean; data: any }>(`/onboarding/${orgId}/resume`),
    },
};

export default api;
